import { useContext, useEffect, useState, useRef } from "react";
import { Box, Button, Container, IconButton, LinearProgress, Typography } from "@mui/material";
import bkstApi from "../../api";
import currency from "currency.js";
import SearchIcon from "@mui/icons-material/Search";
import { UserContext } from "../../context/UserContext";
import BDatePicker from "../../common/input/BDatePicker";
import { useLocation } from "react-router-dom";
import { getQueryParam, getCustomerName, equalsIgnoreCase, getStoreFilterStreets, orderMatchesStoreFilter } from "../../common/util";
import { useNavigate } from "react-router";
import { DataGrid } from "@mui/x-data-grid";
import moment from "moment";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Link as RouterLink } from "react-router-dom";
import ItemFilter from "../../planner/component/ItemFilter";
import { LineChart } from "@mui/x-charts/LineChart";
import { STORAGE_BUCKET } from "../../common/constants";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

const summaryColumns = [
  {
    field: "date",
    headerName: "Date",
    renderCell: (params) => (
      <Box py={1}>
        <Typography variant="overline" color="textSecondary" display="block">
          {moment(params.value, "YYYY-MM-DD").format("ddd")}
        </Typography>
        <Typography variant="body2" display="block">
          {moment(params.value, "YYYY-MM-DD").format("MMM D, YYYY")}
        </Typography>
      </Box>
    ),
    width: 130,
  },
  {
    field: "orderCount",
    headerName: "Orders",
    type: "number",
    width: 100,
  },
  {
    field: "subtotal",
    headerName: "Subtotal",
    type: "number",
    valueFormatter: ({ value }) => currency(value).format(),
    width: 120,
  },
  {
    field: "delivery",
    headerName: "Delivery",
    type: "number",
    valueFormatter: ({ value }) => currency(value).format(),
    width: 120,
  },
  {
    field: "discount",
    headerName: "Discount",
    type: "number",
    valueFormatter: ({ value }) => currency(value).format(),
    width: 120,
  },
  {
    field: "tips",
    headerName: "Tips",
    type: "number",
    valueFormatter: ({ value }) => currency(value).format(),
    width: 120,
  },
  {
    field: "salesTax",
    headerName: "Sales Tax",
    type: "number",
    valueFormatter: ({ value }) => currency(value).format(),
    width: 120,
  },
  {
    field: "platformFee",
    headerName: "Convenience Fee",
    type: "number",
    valueFormatter: ({ value }) => currency(value).format(),
    width: 140,
  },
  {
    field: "total",
    headerName: "Total",
    type: "number",
    valueFormatter: ({ value }) => currency(value).format(),
    width: 120,
  },
  {
    field: "refunds",
    headerName: "Refunds",
    type: "number",
    valueFormatter: ({ value }) => currency(value).format(),
    width: 120,
  },
];

const columns = [
  {
    field: "time",
    headerName: "Timestamp",
    type: "number",
    renderCell: (params) => (
      <div>
        <Box py={1}>
          <Typography variant="caption" display="block">
            {moment(params.value).format("ll")}
          </Typography>
          <Typography variant="overline" display="block" color="textSecondary">
            {moment(params.value).format("LT")}
          </Typography>
        </Box>
      </div>
    ),
    width: 120,
  },
  {
    field: "name",
    headerName: "Customer",
    width: 160,
  },
  {
    field: "subtotal",
    headerName: "Subtotal",
    type: "number",
    valueFormatter: ({ value }) => currency(value).format(),
    width: 100,
  },
  {
    field: "delivery",
    headerName: "Delivery",
    type: "number",
    valueFormatter: ({ value }) => currency(value).format(),
    width: 100,
  },
  {
    field: "discount",
    headerName: "Discount",
    type: "number",
    valueFormatter: ({ value }) => currency(value).format(),
    width: 100,
  },
  {
    field: "tips",
    headerName: "Tips",
    type: "number",
    valueFormatter: ({ value }) => currency(value).format(),
    width: 100,
  },
  {
    field: "salesTax",
    headerName: "Sales Tax",
    type: "number",
    valueFormatter: ({ value }) => currency(value).format(),
    width: 100,
  },
  {
    field: "platformFee",
    headerName: "Convenience Fee",
    type: "number",
    valueFormatter: ({ value }) => currency(value).format(),
    width: 140,
  },
  {
    field: "total",
    headerName: "Total",
    type: "number",
    valueFormatter: ({ value }) => currency(value).format(),
    width: 100,
  },
  {
    field: "refunds",
    headerName: "Refunds",
    type: "number",
    valueFormatter: ({ value }) => currency(value).format(),
    width: 100,
  },
  {
    field: "id",
    headerName: "Order",
    renderCell: (params) => (
      <IconButton component={RouterLink} to={`/order/${params.value}`}>
        <VisibilityOutlinedIcon />
      </IconButton>
    ),
    width: 50,
  },
];

export default function RevenueRecognitionReport(props) {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  let location = useLocation();

  const [fromDt, setFromDt] = useState("");
  const [toDt, setToDt] = useState("");

  const [orders, setOrders] = useState([]);

  const [filter, setFilter] = useState();
  const [filteredResults, setFilteredResults] = useState([]);

  const [loading, setLoading] = useState(false);

  const summaryApiRef = useRef(null);
  const allOrdersApiRef = useRef(null);
  const storeLocations = user?.config?.storeLocation || [];
  const storeStreets = getStoreFilterStreets(user);
  const showStoreFilter = storeStreets.length > 1;

  // Check params to identify search criteria
  useEffect(() => {
    let [qFrom, qTo] = [
      getQueryParam(location.search, "from"),
      getQueryParam(location.search, "to"),
    ];
    if (!qFrom || !qTo) {
      // reset dates
      qFrom = moment().subtract(7, "days").format("YYYY-MM-DD");
      qTo = moment().format("YYYY-MM-DD");
    }
    search(qFrom, qTo);
  }, [location.search]);

  useEffect(() => {
    if (toDt < fromDt) {
      setToDt("");
    }
  }, [fromDt]);

  useEffect(() => {
    let arr = [];
    arr = orders.filter((o) => {
      // check filters
      for (const [k, v] of Object.entries(filter)) {
        if (v.value) {
          if (k === "type") {
            if (v.value === "standard" && o.type !== "shop") {
              return false;
            } else if (v.value === "custom" && o.type === "shop") {
              return false;
            }
          } else if (k === "fulfillment" && !equalsIgnoreCase(o.fulfillment.type, v.value)) {
            return false;
          } else if (k === "store") {
            if (!orderMatchesStoreFilter(o, v.value, storeLocations)) {
              return false;
            }
          }
        }
      }
      // passed all filters
      return true;
    });
    setFilteredResults(arr);
  }, [filter, orders]);

  const runDateSearch = (e) => {
    e.preventDefault();
    navigate(`/report/revenue-recognition?from=${fromDt}&to=${toDt}`);
  };

  const search = (fromDate, toDate) => {
    setLoading(true);
    bkstApi.get(`/my/report/revenue-recognition?fromDt=${fromDate}&toDt=${toDate}`).then((res) => {
      setOrders(res.data.orders);
      setFilter("");

      setFromDt(fromDate);
      setToDt(toDate);

      setLoading(false);
    });
  };

  const exportSummaryToCSV = () => {
    if (summaryApiRef.current) {
      summaryApiRef.current.exportDataAsCsv({
        fileName: `Revenue_Recognition_Summary_${fromDt}_${toDt}`,
        fields: [
          "date",
          "orderCount",
          "subtotal",
          "delivery",
          "discount",
          "tips",
          "salesTax",
          "platformFee",
          "total",
          "refunds",
        ],
      });
    }
  };

  const exportAllOrdersToCSV = () => {
    if (allOrdersApiRef.current) {
      allOrdersApiRef.current.exportDataAsCsv({
        fileName: `Revenue_Recognition_Orders_${fromDt}_${toDt}`,
        fields: [
          "time",
          "name",
          "subtotal",
          "delivery",
          "discount",
          "tips",
          "salesTax",
          "platformFee",
          "total",
          "refunds",
        ],
      });
    }
  };

  if (loading) {
    return <LinearProgress />;
  } else {
    let rollup = {};
    for (const order of filteredResults) {
      const key = order.fulfillment.date;

      let arr = rollup[key]?.orders || [];
      arr.push(order);
      rollup[key] = {
        orders: arr,
        subtotal: arr.reduce((acc, o) => +acc + +o.payment.subtotal || 0, 0),
        delivery: arr.reduce((acc, o) => +acc + +o.payment.delivery || 0, 0),
        discount: arr.reduce((acc, o) => +acc + +o.payment.discount || 0, 0),
        tips: arr.reduce((acc, o) => +acc + +o.payment.tip || 0, 0),
        salesTax: arr.reduce((acc, o) => +acc + +o.payment.salesTax || 0, 0),
        platformFee: arr.reduce((acc, o) => +acc + +o.payment.platformFee || 0, 0),
        total: arr.reduce(
          (acc, o) => +acc + +(o.src === "mkt" ? o.payment.bakerTotal : o.payment.total),
          0
        ),
        refunds: arr.reduce(
          (acc, o) =>
            +acc +
            +(o.payment.refund || [])
              ?.filter((r) => r.reason !== "adjustment")
              .reduce((acc, obj) => {
                return +acc + +obj.amount;
              }, 0),
          0
        ),
      };
    }
    const summaryByDate = Object.entries(rollup)
      .sort(([k1, v1], [k2, v2]) => k1.localeCompare(k2))
      .map(([k, v]) => {
        return {
          date: k,
          subtotal: v.subtotal,
          delivery: v.delivery,
          discount: v.discount,
          tips: v.tips,
          salesTax: v.salesTax,
          platformFee: v.platformFee,
          total: v.total,
          refunds: v.refunds,
          orders: v.orders,
        };
      });

    return (
      <Box>
        <Container maxWidth="md">
          <Box my={4}>
            <Typography variant="h1" gutterBottom>
              Revenue Recognition Report
            </Typography>
            <Typography variant="subtitle1" color="textSecondary" gutterBottom>
              BakeStreet follows Accrual-based Accounting, where revenue is recognized when the
              order is fulfilled, not when the payment is received. This aligns with Generally
              Accepted Accounting Principles (GAAP) and ensures that your sales, refunds, taxes &
              tips are recognized accurately.
            </Typography>
            <Box my={3}>
              <form onSubmit={runDateSearch}>
                <Box sx={{ display: "flex", gap: "10px" }}>
                  <BDatePicker
                    value={fromDt}
                    variant="filled"
                    label="From Date"
                    onChange={(d) => setFromDt(d)}
                    required
                    autoOk
                    disableFuture
                    hideIcon={true}
                  />
                  <BDatePicker
                    value={toDt}
                    variant="filled"
                    label="To Date"
                    onChange={(d) => setToDt(d)}
                    required
                    autoOk
                    disableFuture
                    hideIcon={true}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    style={{ height: "56px" }}
                  >
                    <SearchIcon />
                  </Button>
                </Box>
              </form>
            </Box>
          </Box>
          <Box>
            {orders && orders.length > 0 && (
              <Box>
                <Box my={2}>
                  <ItemFilter
                    config={[
                      { name: "type", options: ["custom", "standard"] },
                      { name: "fulfillment", options: orders.map((o) => o.fulfillment?.type) },
                      showStoreFilter ? { name: "store", options: storeStreets } : "",
                    ]}
                    data={orders}
                    onChange={(o) => setFilter({ ...filter, ...o })}
                  />
                </Box>
              </Box>
            )}
            {summaryByDate && summaryByDate.length > 1 && (
              <Box sx={{ backgroundColor: "#ECECEC", borderRadius: "4px" }}>
                <LineChart
                  dataset={summaryByDate.map((o) => {
                    return { date: moment(o.date, "YYYY-MM-DD").format("MMM D"), sale: o.total };
                  })}
                  xAxis={[{ scaleType: "point", dataKey: "date" }]}
                  series={[
                    {
                      dataKey: "sale",
                      color: "#78374a",
                      label: "Sales (USD)",
                      showMark: ({ index }) => index % 2 === 0,
                    },
                  ]}
                  height={250}
                />
              </Box>
            )}

            {summaryByDate.length > 0 && (
              <Box my={4}>
                <Typography variant="overline">Order Summary by Date</Typography>
                <Box
                  sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                >
                  <Box>
                    <Typography variant="h5">
                      {currency(
                        filteredResults.reduce(
                          (acc, o) =>
                            +acc + +(o.src === "mkt" ? o.payment.bakerTotal : o.payment.total),
                          0
                        ),
                        { precision: 0 }
                      ).format()}{" "}
                      | {filteredResults.length} Orders{" "}
                    </Typography>
                  </Box>
                  <IconButton onClick={exportSummaryToCSV}>
                    <FileDownloadOutlinedIcon />
                  </IconButton>
                </Box>
                <Box sx={{ width: "100%", display: "grid" }}>
                  <DataGrid
                    apiRef={summaryApiRef}
                    rows={summaryByDate.map((o) => ({
                      id: o.date,
                      date: o.date,
                      orderCount: o.orders.length,
                      subtotal: o.subtotal,
                      delivery: o.delivery,
                      discount: o.discount,
                      tips: o.tips,
                      salesTax: o.salesTax,
                      platformFee: o.platformFee,
                      total: o.total,
                      refunds: o.refunds,
                    }))}
                    columns={summaryColumns}
                    autoHeight
                    getRowHeight={() => 60}
                    disableRowSelectionOnClick
                    sx={{
                      "& .MuiDataGrid-cell": {
                        display: "flex",
                        alignItems: "center",
                        padding: "8px",
                      },
                    }}
                    initialState={{
                      sorting: {
                        sortModel: [{ field: "date", sort: "desc" }],
                      },
                    }}
                  />
                </Box>
              </Box>
            )}
            {filteredResults.length > 0 && (
              <Box my={4}>
                <Box
                  sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                >
                  <Typography variant="h5" gutterBottom>
                    All Orders
                  </Typography>
                  <IconButton onClick={exportAllOrdersToCSV}>
                    <FileDownloadOutlinedIcon />
                  </IconButton>
                </Box>
                <Box sx={{ width: "100%", display: "grid" }}>
                  <DataGrid
                    apiRef={allOrdersApiRef}
                    rows={filteredResults.map((od) => {
                      const refundAmount =
                        od.payment.refund
                          ?.filter((r) => r.reason !== "adjustment")
                          .reduce((acc, obj) => +acc + +obj.amount, 0) || 0;
                      return {
                        id: od.id,
                        time: od.ts._seconds * 1000,
                        name: getCustomerName(od.customer),
                        subtotal: od.payment.subtotal,
                        delivery: od.payment.delivery,
                        discount: od.payment.discount,
                        tips: od.payment.tip,
                        salesTax: od.payment.salesTax,
                        platformFee: od.payment.platformFee,
                        total: od.payment.total,
                        refunds: refundAmount,
                        type: od.type,
                      };
                    })}
                    columns={columns}
                    autoHeight
                    getRowHeight={(params) => "auto"}
                    disableRowSelectionOnClick
                    initialState={{
                      sorting: {
                        sortModel: [{ field: "time", sort: "desc" }],
                      },
                    }}
                  />
                </Box>
              </Box>
            )}
            {filteredResults.length === 0 && (
              <Box my={2} px={2} pb={2} textAlign={"center"} sx={{ border: "1px solid #DDD" }}>
                <img
                  style={{ width: "250px" }}
                  src={`${STORAGE_BUCKET}/img/icon/search-results.svg`}
                />
                <Typography variant="subtitle1">Nothing found</Typography>
                <Typography variant="body1" color="textSecondary">
                  Please change your search criteria & try again.
                </Typography>
              </Box>
            )}
          </Box>
        </Container>
      </Box>
    );
  }
}
